import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Radio, SignalHigh, SignalMedium, SignalLow, SignalZero } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/utils';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { UserRole } from '@/types/api';
import { formatUnitOfMeasureSuffix } from '../utils/salt-config';
import { DeviceType } from '@/types/api';
import type { Device, MeterReading, Gateway } from '@/types/api';

const ALARM_LABELS: { key: keyof MeterReading; label: string }[] = [
  { key: 'pipeEmpty', label: 'Pipe Empty' },
  { key: 'noUsage30Days', label: 'No Usage (30 Days)' },
  { key: 'minTemperatureExceeded', label: 'Min Temperature Exceeded' },
  { key: 'maxTemperatureExceeded', label: 'Max Temperature Exceeded' },
  { key: 'lowSignal', label: 'Low Signal' },
  { key: 'burstPipeDetected', label: 'Burst Pipe Detected' },
  { key: 'leakDetected', label: 'Leak Detected' },
  { key: 'reverseFlowDetected', label: 'Reverse Flow Detected' },
  { key: 'lowBattery', label: 'Low Battery' },
  { key: 'lowTemperature', label: 'Low Temperature (SALT)' },
  { key: 'sensusReadError', label: 'Sensus Read Error' },
  { key: 'coverRemoved', label: 'Cover Removed' },
];

function getActiveAlarms(reading: MeterReading): string[] {
  return ALARM_LABELS
    .filter(({ key }) => reading[key] === true)
    .map(({ label }) => label);
}

interface DeviceReadingsTabProps {
  // Used to gate SALT-only display rows in the per-reading detail modal
  // (raw decoder value). May 14 batch: regular meters no longer show raw.
  device: Device;
  readings: MeterReading[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  /** Known gateways for linking Gateway EUI */
  gateways?: Gateway[];
}

export function DeviceReadingsTab({
  device,
  readings,
  totalCount,
  totalPages,
  currentPage,
  onPageChange,
  loading,
  gateways = [],
}: DeviceReadingsTabProps) {
  const isSalt = device.deviceType === DeviceType.SALTTransmitter;
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  const isSysAdmin = activeRole === UserRole.Owner;
  const [selectedReading, setSelectedReading] = useState<MeterReading | null>(null);
  const selectedAlarms = selectedReading ? getActiveAlarms(selectedReading) : [];

  const matchedGateway = selectedReading?.gatewayEui
    ? gateways.find(g => g.gatewayEui.toLowerCase() === selectedReading.gatewayEui!.toLowerCase())
    : null;

  if (readings.length === 0 && !loading) {
    return (
      <Card variant="elevated">
        <EmptyState
          icon={<AlertTriangle className="w-16 h-16" />}
          title="No readings yet"
          description="No meter readings have been received for this device."
        />
      </Card>
    );
  }

  return (
    <>
      <Card variant="elevated" padding="none">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="whitespace-nowrap">
                <TableHead>Timestamp</TableHead>
                <TableHead>Total Usage</TableHead>
                <TableHead>Temp (°F)</TableHead>
                <TableHead>Signal</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Alerts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {readings.map((r) => {
                const alarms = getActiveAlarms(r);
                const hasAlarms = alarms.length > 0;
                return (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    onClick={() => setSelectedReading(r)}
                  >
                    <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatDateTime(r.timestamp)}
                    </TableCell>
                    <TableCell className="text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {(() => {
                        const total = r.computedWaterTotalization ?? r.waterTotalization;
                        const unit = r.computedUnitOfMeasure ?? r.unitOfMeasure;
                        // Defensive: if the backend returns null for both
                        // totalization fields (e.g. historical reading whose
                        // SALT-config lookup didn't resolve), fall back to an
                        // em-dash instead of crashing the row on .toFixed().
                        if (total == null || !Number.isFinite(total)) {
                          return <span className="text-gray-400">—</span>;
                        }
                        const suffix = formatUnitOfMeasureSuffix(unit);
                        return suffix === 'pulses'
                          ? `${total.toFixed(0)} pulses`
                          : `${total.toFixed(2)} ${suffix}`;
                      })()}
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 dark:text-gray-300">
                      {r.temperatureF != null ? r.temperatureF.toFixed(1) : '—'}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {r.rssi != null ? (
                        <span className="inline-flex items-center gap-1.5 font-mono text-gray-600 dark:text-gray-400">
                          {r.rssi >= -80 ? (
                            <SignalHigh className="w-4 h-4 text-success-500" />
                          ) : r.rssi >= -100 ? (
                            <SignalMedium className="w-4 h-4 text-warning-500" />
                          ) : r.rssi >= -110 ? (
                            <SignalLow className="w-4 h-4 text-warning-600" />
                          ) : (
                            <SignalZero className="w-4 h-4 text-error-500" />
                          )}
                          {r.rssi} dBm
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.unitId ? (
                        <button
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                          onClick={(e) => { e.stopPropagation(); navigate(`/units/${r.unitId}`); }}
                        >
                          {r.unitNumber ?? 'Unit'}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasAlarms ? (
                        <span className="inline-flex items-center gap-1.5 text-error-600 font-medium text-xs">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {alarms.length}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-center sm:justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <p className="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
              {totalCount} total readings
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Reading Detail Modal */}
      <Modal
        isOpen={selectedReading !== null}
        onClose={() => setSelectedReading(null)}
        title="Transmission Details"
        description={selectedReading ? formatDateTime(selectedReading.timestamp) : undefined}
        size="md"
      >
        {selectedReading && (
          <div className="space-y-5">
            {/* Reading data — Total Usage is the calibrated dial value the
                user reads on the physical meter; Raw is the decoder's
                pre-calibration count, kept beside it so techs can verify
                the multiplier/baseline math against the audit-of-truth
                value without bouncing back to the device detail page. */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Total Usage</p>
                <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">
                  {(() => {
                    const total = selectedReading.computedWaterTotalization ?? selectedReading.waterTotalization;
                    const unit = selectedReading.computedUnitOfMeasure ?? selectedReading.unitOfMeasure;
                    if (total == null || !Number.isFinite(total)) return '—';
                    const suffix = formatUnitOfMeasureSuffix(unit);
                    return suffix === 'pulses'
                      ? `${total.toFixed(0)} pulses`
                      : `${total.toFixed(2)} ${suffix}`;
                  })()}
                </p>
              </div>
              {isSalt && (
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Raw reading</p>
                  <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">
                    {(() => {
                      const raw = selectedReading.waterTotalization;
                      if (raw == null || !Number.isFinite(raw)) return '—';
                      const suffix = formatUnitOfMeasureSuffix(selectedReading.unitOfMeasure);
                      return suffix === 'pulses'
                        ? `${raw.toFixed(0)} pulses`
                        : `${raw.toFixed(2)} ${suffix}`;
                    })()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Temperature</p>
                <p className="text-gray-900 dark:text-gray-100 font-medium">
                  {selectedReading.temperatureF != null ? `${selectedReading.temperatureF.toFixed(1)} °F` : '—'}
                </p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">Unit</p>
                {selectedReading.unitId ? (
                  <button
                    className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    onClick={() => { setSelectedReading(null); navigate(`/units/${selectedReading.unitId}`); }}
                  >
                    {selectedReading.unitNumber ?? 'Unit'}
                  </button>
                ) : (
                  <p className="text-gray-900 dark:text-gray-100 font-medium">—</p>
                )}
              </div>
            </div>

            {/* Gateway EUI */}
            {selectedReading.gatewayEui && (
              <div className="text-sm">
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Gateway</p>
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-gray-400" />
                  {matchedGateway ? (
                    <button
                      onClick={() => { setSelectedReading(null); navigate(`/gateways/${matchedGateway.id}`); }}
                      className="font-mono text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {selectedReading.gatewayEui}
                    </button>
                  ) : (
                    <span className="font-mono text-gray-600 dark:text-gray-400">{selectedReading.gatewayEui}</span>
                  )}
                  {matchedGateway && (
                    <span className="text-gray-500 dark:text-gray-400">({matchedGateway.name})</span>
                  )}
                </div>
              </div>
            )}

            {/* Radio metadata */}
            {(selectedReading.rssi != null || selectedReading.snr != null) && (
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Signal</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  {selectedReading.rssi != null && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">RSSI</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">{selectedReading.rssi} dBm</p>
                    </div>
                  )}
                  {selectedReading.snr != null && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">SNR</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">{selectedReading.snr} dB</p>
                    </div>
                  )}
                  {selectedReading.spreadingFactor != null && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">SF / BW</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">
                        SF{selectedReading.spreadingFactor}{selectedReading.bandwidth ? ` / ${selectedReading.bandwidth} kHz` : ''}
                      </p>
                    </div>
                  )}
                  {selectedReading.frequency && (
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">Frequency</p>
                      <p className="text-gray-900 dark:text-gray-100 font-mono font-medium">{selectedReading.frequency}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Alarms */}
            {selectedAlarms.length > 0 && (
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-2">Active Alarms</p>
                <div className="space-y-1.5">
                  {selectedAlarms.map((label) => (
                    <div key={label} className="flex items-center gap-2 text-sm text-error-700 dark:text-error-500">
                      <span className="w-2 h-2 rounded-full bg-error-500 flex-shrink-0" />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedAlarms.length === 0 && (
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Alarms</p>
                <Badge variant="success" size="sm">None</Badge>
              </div>
            )}

            {/* Raw Payload — Sys Admin only */}
            {isSysAdmin && selectedReading.rawPayload && (
              <details className="text-sm">
                <summary className="text-gray-400 text-xs uppercase tracking-wide cursor-pointer hover:text-gray-600 dark:hover:text-gray-300">
                  Raw Payload
                </summary>
                <pre className="mt-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-xs font-mono text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-pre-wrap break-all">
                  {(() => {
                    try { return JSON.stringify(JSON.parse(selectedReading.rawPayload!), null, 2); }
                    catch { return selectedReading.rawPayload; }
                  })()}
                </pre>
              </details>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
